import { useEffect, useState } from "react";
import { Gift, Copy, Share2, UserPlus, Crown, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const STEPS = [
  [Share2, "Partage ton lien", "Envoie-le à un camarade de classe."],
  [UserPlus, "Il crée son compte", "Grâce à ton lien, il est rattaché à toi."],
  [Crown, "Il s'abonne", "Tu gagnes 1 mois Premium offert, cumulable."],
];

export default function Referral() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referral").then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e))); }, []);
  const link = data ? `${window.location.origin}/?parrain=${data.code}` : "";
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Lien copié !"); };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "NoteMyClass AI", text: "Je prends mes notes de cours avec NoteMyClass AI, essaie avec mon lien :", url: link }); } catch { /* cancelled */ }
    } else copy();
  };
  const active = data?.premium_until && new Date(data.premium_until) > new Date();

  return (
    <div data-testid="referral-page" className="max-w-4xl">
      <p className="eyebrow">Parrainage</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Invite un camarade, gagne <span className="text-brand">1 mois Premium</span></h1>
      <p className="mt-2 text-sm text-muted-foreground">Pour chaque camarade qui s'abonne grâce à ton lien, tu reçois un mois Premium offert. Les mois se cumulent.</p>

      <div className="bg-brand relative mt-8 overflow-hidden rounded-3xl p-7 text-white">
        <Gift className="absolute -right-6 -top-6 h-40 w-40 opacity-10" />
        <p className="text-sm text-white/80">Ton lien de parrainage</p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <code data-testid="referral-link" className="flex-1 truncate rounded-full bg-white/15 px-5 py-3 font-mono text-sm backdrop-blur">{link || "…"}</code>
          <button data-testid="referral-copy-button" onClick={copy} disabled={!data} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-transform hover:-translate-y-0.5"><Copy className="h-4 w-4" /> Copier</button>
          <button data-testid="referral-share-button" onClick={share} disabled={!data} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-5 py-3 text-sm font-semibold"><Share2 className="h-4 w-4" /> Partager</button>
        </div>
        <p className="mt-3 text-xs text-white/75">Ton code : <b data-testid="referral-code" className="font-mono">{data?.code}</b></p>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[["Camarades inscrits", data?.invited ?? 0, "referral-invited-count"], ["Abonnés grâce à toi", data?.subscribed ?? 0, "referral-subscribed-count"], ["Mois Premium gagnés", data?.months_earned ?? 0, "referral-months-count"]].map(([l, v, t]) => (
          <div key={t} className="card-flat p-5"><p className="eyebrow">{l}</p><p data-testid={t} className="font-display mt-2 text-4xl font-extrabold">{v}</p></div>
        ))}
      </div>
      {active && <p data-testid="referral-premium-until" className="mt-4 flex items-center gap-2 text-sm"><CalendarCheck className="h-4 w-4 text-primary" /> Premium offert jusqu'au <b>{fmtDate(data.premium_until)}</b></p>}

      <h2 className="mt-12 text-xl font-bold">Comment ça marche</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {STEPS.map(([Icon, t, d], i) => (
          <div key={t} className="card-flat p-5"><span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Icon className="h-5 w-5" /></span><p className="mt-4 font-semibold">{i + 1}. {t}</p><p className="mt-1 text-sm text-muted-foreground">{d}</p></div>
        ))}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Le parrainage s'applique uniquement aux nouveaux comptes créés avec ton lien. Si tu as déjà un abonnement payant, tes mois offerts s'appliqueront dès que tu repasses à l'offre Gratuite pendant leur validité.</p>
    </div>
  );
}
